import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  ADMIN_USER_ROLE_TIER,
  type AdminUserRole,
} from '@shared/types';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { landingPathsFor } from '../games/registry';
import { adminHomePath } from '../utils/adminAccess';

interface RequireAdminProps {
  /** Minimum role tier required. Default 'viewer' — any tier-ladder admin. */
  minRole?: AdminUserRole;
  /**
   * Extra roles permitted REGARDLESS of tier. OR-combined with the `minRole`
   * tier check. This is how the lateral `content_manager` role (tier 0,
   * below `viewer`) is admitted to the content surface without granting it
   * the tier-gated dashboards. Mirror of the server `requireAdminRoleIn`.
   */
  allowRoles?: readonly AdminUserRole[];
  children: ReactNode;
}

/**
 * Route guard that redirects unauthenticated visitors to `/login` and
 * authenticated admins who lack access to THIS area to their own home page
 * (so a content manager deep-linking a tier-gated page lands on the active
 * game's content landing — its manifest's `landingPaths` —
 * not a dead login screen). There is no `'none'` sentinel — admins live in a
 * separate collection, so "not an admin" is the absence of `adminUser`.
 *
 * Access = `tier(role) >= tier(minRole)` OR `role ∈ allowRoles`.
 */
export function RequireAdmin({
  minRole = 'viewer',
  allowRoles,
  children,
}: RequireAdminProps) {
  const token = useAuthStore((s) => s.token);
  const status = useAuthStore((s) => s.status);
  const adminUser = useAuthStore((s) => s.adminUser);
  const activeGameId = useGameStore((s) => s.activeGameId);
  const location = useLocation();

  const required = ADMIN_USER_ROLE_TIER[minRole];
  const userTier = adminUser ? ADMIN_USER_ROLE_TIER[adminUser.role] : 0;

  // While the bootstrap /admin-refresh probe is in flight, do NOT redirect —
  // that would flash the login form for users who DO have a valid refresh
  // cookie. Show a neutral placeholder; the probe is fast (<300 ms typical)
  // and races route mounting on cold load.
  if (status === 'hydrating') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm"
        data-testid="require-admin-hydrating"
      >
        Restoring session…
      </div>
    );
  }

  // Not authenticated at all → login (preserve intended destination).
  if (!token || !adminUser) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  const permitted =
    userTier >= required || (allowRoles?.includes(adminUser.role) ?? false);

  // Authenticated, but this area is out of scope for the role → send them to
  // a page they CAN use rather than bouncing a logged-in admin to /login.
  if (!permitted) {
    return (
      <Navigate
        to={adminHomePath(adminUser.role, landingPathsFor(activeGameId))}
        replace
      />
    );
  }

  return <>{children}</>;
}
